import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import type { Position } from '../../types.js';

// Function to create arrow icon pointing in a specific direction
const createArrowIcon = (bearing: number) => {
  // Get the actual color value from CSS variables
  const neutralColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-neutral').trim() || '#8a9a91';
    
  return L.divIcon({
    className: 'route-arrow',
    html: `
      <div style="
        width: 0; 
        height: 0; 
        border-left: 3px solid transparent; 
        border-right: 3px solid transparent; 
        border-bottom: 6px solid ${neutralColor}; 
        transform: rotate(${bearing}deg);
        filter: drop-shadow(0 0 1px rgba(0,0,0,0.3));
      "></div>
    `,
    iconSize: [6, 6],
    iconAnchor: [3, 3]
  });
};

// Function to calculate accurate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
           Math.cos(φ1) * Math.cos(φ2) *
           Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
};

// Function to interpolate a point along a line segment
const interpolatePoint = (start: Position, end: Position, ratio: number): Position => {
  return {
    lat: start.lat + (end.lat - start.lat) * ratio,
    lng: start.lng + (end.lng - start.lng) * ratio
  };
};

// Function to calculate bearing between two points
const calculateBearing = (start: Position, end: Position): number => {
  const lat1 = start.lat * Math.PI / 180;
  const lat2 = end.lat * Math.PI / 180;
  const deltaLng = (end.lng - start.lng) * Math.PI / 180;

  const x = Math.sin(deltaLng) * Math.cos(lat2);
  const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

  const bearing = Math.atan2(x, y) * 180 / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360 degrees
};

// Simplified function to generate arrow positions every 100px on screen
const generateArrowPositions = (route: Position[], zoomLevel: number): Array<{position: Position, bearing: number}> => {
  if (route.length < 2) return [];

  const arrows: Array<{position: Position, bearing: number}> = [];
  
  // Calculate distance for 100px at current zoom level
  // At zoom 13: ~100px = ~300m, zoom 15: ~100px = ~75m, zoom 18: ~100px = ~10m
  const pixelDistance = 100; // pixels
  const metersPerPixel = 156543.03392 * Math.cos(route[0].lat * Math.PI / 180) / Math.pow(2, zoomLevel);
  const targetSpacing = pixelDistance * metersPerPixel;
  
  let accumulatedDistance = 0;

  // Always add first arrow
  if (route.length >= 2) {
    const firstBearing = calculateBearing(route[0], route[1]);
    arrows.push({
      position: route[0],
      bearing: firstBearing
    });
  }

  for (let i = 1; i < route.length; i++) {
    const segmentDistance = calculateDistance(
      route[i-1].lat, route[i-1].lng,
      route[i].lat, route[i].lng
    );
    
    accumulatedDistance += segmentDistance;
    
    // Place arrow every targetSpacing meters (which equals 100px on screen)
    if (accumulatedDistance >= targetSpacing) {
      const bearing = calculateBearing(route[i-1], route[i]);
      
      // If the segment is longer than our target spacing, interpolate positions
      if (segmentDistance > targetSpacing && accumulatedDistance > targetSpacing) {
        const segmentsNeeded = Math.floor(segmentDistance / targetSpacing);
        for (let j = 1; j <= segmentsNeeded; j++) {
          const ratio = j / (segmentsNeeded + 1);
          const interpolatedPos = interpolatePoint(route[i-1], route[i], ratio);
          arrows.push({
            position: interpolatedPos,
            bearing: bearing
          });
        }
      } else {
        // Add arrow at current position
        arrows.push({
          position: route[i],
          bearing: bearing
        });
      }
      
      accumulatedDistance = 0;
    }
  }

  // Always add final arrow
  if (route.length >= 2) {
    const lastBearing = calculateBearing(route[route.length-2], route[route.length-1]);
    arrows.push({
      position: route[route.length-1],
      bearing: lastBearing
    });
  }

  return arrows;
};

interface MobileRouteLayerProps {
  route: Position[];
  onHover: (index: number | null) => void;
  setSnapPoint: (point: Position | null) => void;
  onRouteRendered?: () => void; // Callback when route is fully rendered
}

/**
 * Mobile-optimized route layer for mobile devices and complex routes.
 * Uses direct Leaflet API with Canvas renderer for better performance
 * and continuous line rendering on touch devices.
 */
export const MobileRouteLayer: React.FC<MobileRouteLayerProps> = ({
  route,
  onHover: _onHover,
  setSnapPoint: _setSnapPoint,
  onRouteRendered
}) => {
  const map = useMap();
  const polylineRef = useRef<L.Polyline | null>(null);
  const arrowMarkersRef = useRef<L.Marker[]>([]);
  const [currentZoom, setCurrentZoom] = useState(11);

  // Track zoom level changes
  useEffect(() => {
    const handleZoom = () => {
      setCurrentZoom(map.getZoom());
    };

    // Set initial zoom
    setCurrentZoom(map.getZoom());
    
    // Listen for zoom changes
    map.on('zoomend', handleZoom);
    
    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map]);

  useEffect(() => {
    // Clean up previous polyline
    if (polylineRef.current && map.hasLayer(polylineRef.current)) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    // Clean up previous arrow markers
    arrowMarkersRef.current.forEach(marker => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });
    arrowMarkersRef.current = [];

    if (!route || route.length < 2) {
      return;
    }

    // Validate and convert coordinates to LatLng objects
    const latLngs: L.LatLng[] = [];
    route.forEach((pos) => {
      if (pos && 
          typeof pos.lat === 'number' && 
          typeof pos.lng === 'number' &&
          !isNaN(pos.lat) && !isNaN(pos.lng) &&
          Math.abs(pos.lat) <= 90 && Math.abs(pos.lng) <= 180) {
        latLngs.push(L.latLng(pos.lat, pos.lng));
      }
    });

    if (latLngs.length < 2) {
      return;
    }

    try {
      // Get the actual color value from CSS variables for the polyline
      const accentColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-accent').trim() || '#1e4d40';
        
      // Create polyline with mobile-optimized settings
      polylineRef.current = L.polyline(latLngs, {
        color: accentColor,
        weight: 6,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round',
        smoothFactor: 0.8, // Higher smoothing for mobile performance
        interactive: false,
        // Force continuous line rendering
        renderer: L.canvas() // Use canvas renderer for better mobile performance
      });

      // Add to map
      polylineRef.current.addTo(map);

      // Wait for the polyline to be actually rendered
      // Use a longer timeout to ensure visual rendering is complete
      if (onRouteRendered) {
        setTimeout(() => {
          onRouteRendered();
        }, 100);
      }

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('MobileRouteLayer - Error creating polyline:', error);
      }
    }

    // Cleanup function
    return () => {
      if (polylineRef.current && map.hasLayer(polylineRef.current)) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }

      // Clean up arrow markers
      arrowMarkersRef.current.forEach(marker => {
        if (map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      });
      arrowMarkersRef.current = [];
    };
  }, [route, map]); // Removed onRouteRendered from dependencies

  // Separate effect to manage arrow visibility based on zoom level
  useEffect(() => {
    // Only manage arrows if we have a route and polyline exists
    if (route.length < 2 || !polylineRef.current) return;

    // Clean up existing arrow markers
    arrowMarkersRef.current.forEach(marker => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });
    arrowMarkersRef.current = [];

    // Add arrows only if zoomed in sufficiently
    const showArrows = currentZoom >= 13;
    if (showArrows) {
      const arrowPositions = generateArrowPositions(route, currentZoom);
      arrowPositions.forEach((arrow) => {
        const marker = L.marker(arrow.position, {
          icon: createArrowIcon(arrow.bearing),
          interactive: false
        });
        marker.addTo(map);
        arrowMarkersRef.current.push(marker);
      });
    }
  }, [currentZoom, route, map]);

  return null;
};
